import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import parse from 'html-react-parser';
import { getDecimal } from "../global/utils";
import { useStyles } from '../styles/styles';
import { useTheme } from "@mui/material/styles";
import useMediaQuery from '@mui/material/useMediaQuery'
import Typography from "@mui/material/Typography";
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import FormControl from "@mui/material/FormControl";
import FormLabel from '@mui/material/FormLabel';
import Button from "@mui/material/Button";
import { NumericFormat } from "react-number-format";
import ReplayIcon from "@mui/icons-material/Replay";
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import Select from "@mui/material/Select";

const AddFee = (props) => {
    const payableFee = props.payableFee;
    const addSelected = props.addSelected;
    const allFees = props.allFees;
    const feeClicked = props.feeClicked;
    // Which of this panel's controls the summary error is about, so only the
    // control actually at fault is marked, and only the one focus lands on
    // describes itself with the Alert (CPDEV-2789, 3.3.1).
    const fieldErrors = props.fieldErrors || [];
    const errorId = props.errorId;
    const firstErrorField = fieldErrors.find((e) => e.field)?.field;
    const isInvalid = (field) => (fieldErrors.some((e) => e.field === field) ? 'true' : 'false');
    const describedBy = (field) => (field === firstErrorField ? errorId : undefined);
    const { register, handleSubmit, setValue } = useForm();
    const classes = useStyles;
    const [itemAmount, setItemAmount] = useState(0);
    const [quantity, setQuantity] = useState(0);
    const [totalAmt, setTotalAmt] = useState(0);
    const [selectedFee, setSelectedFee] = useState('');
    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.down('lg'));

    const quantityChanged = (e) => {
        let qty = 0;
        const qtyVal = e.target.value;
        if (qtyVal) qty = parseInt(qtyVal);
        setQuantity(qty);
        if (qty > 0 && itemAmount > 0) {
            setTotalAmt(qty * itemAmount);
        } else {
            setTotalAmt(0);
        }
    }

    const itemAmtChanged = (values) => {
        if (values) {
            const amt = values.floatValue;
            setItemAmount(amt);
            if (quantity > 0) {
                setTotalAmt(amt * quantity);
            } else {
                setTotalAmt(0);
            }
        } else {
            setTotalAmt(getDecimal(0))
        }
    }

    const addSelectedFee = (form) => {
        if (!payableFee) return;
        const newMiscFee = {
            key: Date.now(),
            cmsId: payableFee.cmsId,
            name: payableFee.name,
            amount: totalAmt,
            quantity: quantity,
            itemAmount: itemAmount,
            comments: form.comments,
            deliveryMethod: false
        }
        // Only clear once the fee has been accepted. Clearing regardless wiped the
        // entry the error was asking the user to correct, and disabled Add Fee with it.
        if (addSelected(newMiscFee)) clearFee();
    }

    const clearFee = () => {
        setValue('feeSelect', '');
        setValue('enteredAmount', '');
        setValue('quantity', '');
        setValue('totalAmount', '');
        setValue('comments', '');
        setItemAmount(0);
        setQuantity(0);
        setTotalAmt(0);
        setSelectedFee('');
    }

    const handleFeeChange = (e) => {
        const feeName = e.target.value;
        setSelectedFee(feeName);
        feeClicked(e);
    }

    useEffect(() => {
        if (payableFee) {
            const amt = payableFee.allowAmountEntry ? 0 : payableFee.amount;
            setItemAmount(amt);
            setQuantity(1);
            setTotalAmt(amt);
        }
    }, [payableFee])

    return (
        <>
            <Typography variant={smallScreen ? 'subtitle1' : 'h6'} component='h2' sx={{...classes.title, marginTop: {xs: '0', md: '.5rem'}}}>Add a Fee</Typography>
            <Paper elevation={3} sx={{...classes.tableContainer, padding: '1rem'}}>
                <Grid container spacing={1} style={{width: '100%', fontSize: '.9rem'}}>
                    {allFees && (
                        <Grid
                            size={{
                                xs: 6,
                                md: 3
                            }}>
                            <FormControl component='fieldset' fullWidth sx={classes.formControl} {...register('feeSelect')}>
                                <FormLabel component="legend" htmlFor='feeSelect' sx={classes.inputLabel}>
                                    Fee Name
                                </FormLabel>
                                <Select
                                    id='feeSelect'
                                    name='feeSelect'
                                    native
                                    variant='outlined'
                                    fullWidth
                                    sx={classes.numberbox}
                                    onChange={handleFeeChange}
                                    inputProps={{id: 'feeSelect', 'aria-label': 'Fee Name'}}
                                >
                                    <option key='-1' value=''>None</option>
                                    {allFees.map((fee) => (
                                        <option key={fee.name} value={fee.name} style={{fontSize: '.9rem'}}>{fee.name}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    )}
                    <Grid
                        size={{
                            xs: 6,
                            md: 3
                        }}>
                        <FormControl fullWidth {...register('enteredAmount')}>
                            <FormLabel htmlFor='amount' sx={classes.inputLabel}>
                                {payableFee && payableFee.allowAmountEntry ? 'Enter' : 'Fee'} Amount
                            </FormLabel>
                            <NumericFormat
                                id='amount'
                                name='enteredAmount'
                                customInput={TextField}
                                type='text'
                                autoComplete='off'
                                variant='outlined'
                                allowEmptyFormatting
                                mask="_" decimalScale={2}
                                decimalSeparator='.'
                                fixedDecimalScale={true}
                                value={itemAmount}
                                onValueChange={values => {itemAmtChanged(values)}}
                                slotProps={{
                                    input: {readOnly: payableFee ? !payableFee.allowAmountEntry : true, sx: {...classes.numberbox}, disabled: selectedFee === ''},
                                    htmlInput: {
                                        "aria-invalid": isInvalid('amount'),
                                        "aria-describedby": describedBy('amount'),
                                    }
                                }}
                            />
                        </FormControl>
                    </Grid>
                    <Grid
                        size={{
                            xs: 6,
                            md: 3
                        }}>
                        <FormControl fullWidth>
                            <FormLabel htmlFor='quantity' sx={classes.inputLabel}>
                                Quantity
                            </FormLabel>
                            <TextField
                                id='quantity'
                                name='quantity'
                                variant='outlined'
                                type='number'
                                value={quantity}
                                {...register('quantity')}
                                onChange={quantityChanged}
                                disabled={selectedFee === ''}
                                slotProps={{
                                    htmlInput: {
                                        sx: classes.textbox,
                                        "aria-invalid": isInvalid('quantity'),
                                        "aria-describedby": describedBy('quantity'),
                                    }
                                }} />
                        </FormControl>
                    </Grid>
                    <Grid
                        size={{
                            xs: 6,
                            md: 3
                        }}>
                        <FormControl fullWidth>
                            <FormLabel htmlFor='total' sx={classes.inputLabel}>
                                Total Amount
                            </FormLabel>
                            <Typography
                                id='total'
                                name='totalAmount'
                                sx={{
                                    padding: '0 1rem',
                                    height: '1.9rem',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                {getDecimal(totalAmt, false)}
                            </Typography>
                        </FormControl>
                    </Grid>
                    {payableFee && payableFee.payorInstructions && (
                        <>
                            <Grid
                                sx={{marginTop: '.8rem'}}
                                size={{
                                    xs: 4,
                                    md: 3
                                }}>
                                <Typography variant='body1' component='h5' sx={classes.title}>Entry Instructions:</Typography>
                            </Grid>
                            <Grid
                                sx={{marginTop: '.8rem'}}
                                size={{
                                    xs: 8,
                                    md: 9
                                }}>
                                <Typography variant='subtitle2' color='primary' sx={{paddingTop: '2pt'}}>{parse(payableFee.payorInstructions)}</Typography>
                            </Grid>
                        </>
                    )}
                    {/* Originally written as <Grid xs={12}> with no `item`, so the
                        breakpoint never applied and this row was never sized.
                        Now sized deliberately. */}
                    <Grid size={{xs: 12, md: 9}}>
                        <FormControl fullWidth sx={classes.formControl}>
                            {/* The id was 'request details'. Whitespace is not allowed
                                in an id, so the field could not be addressed with a
                                CSS selector. The aria-label here was also dead: as a
                                JSX prop it landed on the .MuiTextField-root div, not
                                on the textarea - measured, along with the name still
                                computing as "Request Details" from this FormLabel. */}
                            <FormLabel htmlFor='requestDetails' sx={classes.inputLabel}>
                                Request Details
                            </FormLabel>
                            <TextField
                                id='requestDetails'
                                fullWidth
                                multiline
                                rows={3}
                                sx={{resize: 'none'}}
                                {...register('comments')}
                                disabled={selectedFee === ''}
                                slotProps={{
                                    htmlInput: {maxLength: 500, sx: {fontSize: '.9rem', lineHeight: '1rem'}}
                                }}
                            />
                        </FormControl>
                    </Grid>
                    <Grid size={12}>
                        {/* DOM order is the visual order (2.4.3); justifyContent keeps the
                            row right-aligned, which is what row-reverse used to do. */}
                        <Box component='span' sx={{display: 'flex', justifyContent: 'flex-end'}}>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<ReplayIcon/>}
                                sx={classes.squashedButton}
                                onClick={clearFee}
                            >
                                Clear
                            </Button>
                            <Button
                                variant='contained'
                                color='primary'
                                startIcon={<AddShoppingCartIcon />}
                                sx={{...classes.squashedButton, fontSize: smallScreen ? '.6rem' : 'inherit'}}
                                onClick={handleSubmit(addSelectedFee)}
                                disabled={selectedFee === ''}
                            >
                                Add Fee
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>
        </>
    );
}

export default AddFee;